/**
 * Extract RBAC policy from an endpoint or environment group
 * @param {Object} entity - Either an endpoint or environment group
 * @returns {Object|null} Policy object with policyID and policyName, or null if no RBAC policy found
 */
export function getRBACPolicy(entity) {
  if (!entity) {
    return null;
  }

  // Handle endpoints - they have Policies.dockerRBACPolicy or Policies.k8sRBACPolicy
  if (entity.Policies && (entity.Policies.dockerRBACPolicy || entity.Policies.k8sRBACPolicy)) {
    const isK8s = !!entity.Policies.k8sRBACPolicy;
    const rbacPolicy = entity.Policies.dockerRBACPolicy || entity.Policies.k8sRBACPolicy;
    if (rbacPolicy && rbacPolicy.policyID && rbacPolicy.policyName) {
      return {
        policyID: rbacPolicy.policyID,
        policyName: rbacPolicy.policyName,
        policyType: isK8s ? 'rbac-k8s' : 'rbac-docker',
      };
    }
  }

  // Handle environment groups - they have Policies array
  if (entity.Policies && Array.isArray(entity.Policies)) {
    // Find the first RBAC policy (either rbac-k8s or rbac-docker)
    const rbacPolicy = entity.Policies.find((policy) => policy.Type === 'rbac-k8s' || policy.Type === 'rbac-docker');

    if (rbacPolicy && rbacPolicy.Id && rbacPolicy.Name) {
      return {
        policyID: rbacPolicy.Id,
        policyName: rbacPolicy.Name,
        policyType: rbacPolicy.Type,
        policyData: rbacPolicy, // Include the full policy data for extracting access policies
      };
    }
  }

  return null;
}

/**
 * Extract UserAccessPolicies and TeamAccessPolicies from an RBAC policy
 * @param {Object} policy - The RBAC policy object with Data.EnvironmentAccessPolicies
 * @returns {Object} Object with userAccessPolicies and teamAccessPolicies
 */
export function extractAccessPoliciesFromPolicy(policy) {
  if (!policy || !policy.Data || !policy.Data.EnvironmentAccessPolicies) {
    return {
      userAccessPolicies: {},
      teamAccessPolicies: {},
    };
  }

  const envAccessPolicies = policy.Data.EnvironmentAccessPolicies;
  const userAccessPolicies = {};
  const teamAccessPolicies = {};

  // Extract user access policies
  // Structure: UserAccessPolicies[userId] = Directive[AccessPolicy] = { value: { RoleId: X }, enforce: Y }
  if (envAccessPolicies.UserAccessPolicies) {
    Object.keys(envAccessPolicies.UserAccessPolicies).forEach((userId) => {
      const policyDirective = envAccessPolicies.UserAccessPolicies[userId];
      if (policyDirective) {
        // The Directive has a 'value' property containing the AccessPolicy
        const accessPolicy = policyDirective.value || policyDirective;
        if (accessPolicy) {
          // AccessPolicy has RoleId (or RoleID)
          const roleId = accessPolicy.RoleId || accessPolicy.RoleID;
          // Handle case where RoleId itself might be a Directive
          const finalRoleId = roleId && typeof roleId === 'object' && roleId.value !== undefined ? roleId.value : roleId;

          if (finalRoleId !== undefined && finalRoleId !== null) {
            userAccessPolicies[parseInt(userId, 10)] = {
              RoleId: finalRoleId,
            };
          }
        }
      }
    });
  }

  // Extract team access policies
  // Structure: TeamAccessPolicies[teamId] = Directive[AccessPolicy] = { value: { RoleId: X }, enforce: Y }
  if (envAccessPolicies.TeamAccessPolicies) {
    Object.keys(envAccessPolicies.TeamAccessPolicies).forEach((teamId) => {
      const policyDirective = envAccessPolicies.TeamAccessPolicies[teamId];
      if (policyDirective) {
        // The Directive has a 'value' property containing the AccessPolicy
        const accessPolicy = policyDirective.value || policyDirective;
        if (accessPolicy) {
          // AccessPolicy has RoleId (or RoleID)
          const roleId = accessPolicy.RoleId || accessPolicy.RoleID;
          // Handle case where RoleId itself might be a Directive
          const finalRoleId = roleId && typeof roleId === 'object' && roleId.value !== undefined ? roleId.value : roleId;

          if (finalRoleId !== undefined && finalRoleId !== null) {
            teamAccessPolicies[parseInt(teamId, 10)] = {
              RoleId: finalRoleId,
            };
          }
        }
      }
    });
  }

  return {
    userAccessPolicies,
    teamAccessPolicies,
  };
}
